import type { Calibration, ControlPoint, WorldRect } from './types';

/** Least-squares fit of v = s*u + o. Returns null when u has no spread. */
function fitLine(us: number[], vs: number[]): { s: number; o: number } | null {
  const n = us.length;
  if (n < 2) return null;
  let su = 0,
    sv = 0,
    suu = 0,
    suv = 0;
  for (let i = 0; i < n; i++) {
    su += us[i];
    sv += vs[i];
    suu += us[i] * us[i];
    suv += us[i] * vs[i];
  }
  const den = n * suu - su * su;
  if (Math.abs(den) < 1e-9) return null;
  const s = (n * suv - su * sv) / den;
  return { s, o: (sv - s * su) / n };
}

/**
 * Fits x = a*px + c and z = e*py + f independently (no rotation/shear).
 * Needs ≥ 2 control points with distinct px and distinct py; 3+ gives a meaningful residual.
 */
export function fitAxisAligned(points: ControlPoint[]): Calibration | null {
  const fx = fitLine(
    points.map((p) => p.px),
    points.map((p) => p.x),
  );
  const fz = fitLine(
    points.map((p) => p.py),
    points.map((p) => p.z),
  );
  if (!fx || !fz) return null;
  const cal: Calibration = {
    mode: 'fitted',
    affine: [fx.s, 0, fx.o, 0, fz.s, fz.o],
    controlPoints: points,
  };
  cal.rms = residualRms(cal);
  return cal;
}

/** Solves a 3x3 linear system with Gaussian elimination (partial pivoting). */
function solve3(m: number[][], r: number[]): number[] | null {
  const a = m.map((row, i) => [...row, r[i]]);
  for (let col = 0; col < 3; col++) {
    let piv = col;
    for (let row = col + 1; row < 3; row++) if (Math.abs(a[row][col]) > Math.abs(a[piv][col])) piv = row;
    if (Math.abs(a[piv][col]) < 1e-12) return null;
    [a[col], a[piv]] = [a[piv], a[col]];
    for (let row = 0; row < 3; row++) {
      if (row === col) continue;
      const k = a[row][col] / a[col][col];
      for (let c = col; c < 4; c++) a[row][c] -= k * a[col][c];
    }
  }
  return [a[0][3] / a[0][0], a[1][3] / a[1][1], a[2][3] / a[2][2]];
}

/** Full 6-parameter least-squares affine fit. Diagnostic only (M1 draws axis-aligned). Needs ≥ 3 non-collinear points. */
export function fitFullAffine(points: ControlPoint[]): Calibration | null {
  if (points.length < 3) return null;
  // Normal equations: [Σpx² Σpxpy Σpx; Σpxpy Σpy² Σpy; Σpx Σpy n] · [a b c] = [Σpx·x Σpy·x Σx]
  let sxx = 0,
    sxy = 0,
    sx = 0,
    syy = 0,
    sy = 0;
  let rx = [0, 0, 0],
    rz = [0, 0, 0];
  for (const p of points) {
    sxx += p.px * p.px;
    sxy += p.px * p.py;
    sx += p.px;
    syy += p.py * p.py;
    sy += p.py;
    rx = [rx[0] + p.px * p.x, rx[1] + p.py * p.x, rx[2] + p.x];
    rz = [rz[0] + p.px * p.z, rz[1] + p.py * p.z, rz[2] + p.z];
  }
  const m = [
    [sxx, sxy, sx],
    [sxy, syy, sy],
    [sx, sy, points.length],
  ];
  const abc = solve3(m, rx);
  const def = solve3(m, rz);
  if (!abc || !def) return null;
  const cal: Calibration = {
    mode: 'fitted',
    affine: [abc[0], abc[1], abc[2], def[0], def[1], def[2]],
    controlPoints: points,
  };
  cal.rms = residualRms(cal);
  return cal;
}

/** Rotation implied by a full affine fit, in degrees (0 = image is north-up). */
export function affineRotationDeg(cal: Calibration): number {
  const [a, , , d] = cal.affine;
  // Image x axis maps to world (a, d); a north-up image has d ≈ 0.
  return (Math.atan2(d, a) * 180) / Math.PI;
}

export function pixelToWorld(cal: Calibration, px: number, py: number): { x: number; z: number } {
  const [a, b, c, d, e, f] = cal.affine;
  return { x: a * px + b * py + c, z: d * px + e * py + f };
}

export function worldToPixel(cal: Calibration, x: number, z: number): { px: number; py: number } {
  const [a, b, c, d, e, f] = cal.affine;
  const det = a * e - b * d;
  const dx = x - c,
    dz = z - f;
  return { px: (e * dx - b * dz) / det, py: (a * dz - d * dx) / det };
}

export function residualRms(cal: Calibration): number | null {
  if (cal.controlPoints.length === 0) return null;
  let sum = 0;
  for (const p of cal.controlPoints) {
    const w = pixelToWorld(cal, p.px, p.py);
    sum += (w.x - p.x) ** 2 + (w.z - p.z) ** 2;
  }
  return Math.sqrt(sum / cal.controlPoints.length);
}

export function residuals(cal: Calibration): number[] {
  return cal.controlPoints.map((p) => {
    const w = pixelToWorld(cal, p.px, p.py);
    return Math.hypot(w.x - p.x, w.z - p.z);
  });
}

/** World rectangle covered by an image of widthPx × heightPx under an axis-aligned calibration. */
export function imageWorldBounds(cal: Calibration, widthPx: number, heightPx: number): WorldRect {
  const corners = [
    pixelToWorld(cal, 0, 0),
    pixelToWorld(cal, widthPx, 0),
    pixelToWorld(cal, 0, heightPx),
    pixelToWorld(cal, widthPx, heightPx),
  ];
  return {
    minX: Math.min(...corners.map((c) => c.x)),
    maxX: Math.max(...corners.map((c) => c.x)),
    minZ: Math.min(...corners.map((c) => c.z)),
    maxZ: Math.max(...corners.map((c) => c.z)),
  };
}

/** Calibration for an image that exactly covers `rect`, pixel (0,0) at (minX, maxZ) — the Unity top-down render. */
export function exactCalibration(rect: WorldRect, widthPx: number, heightPx: number): Calibration {
  const a = (rect.maxX - rect.minX) / widthPx;
  const e = -(rect.maxZ - rect.minZ) / heightPx;
  return {
    mode: 'exact',
    affine: [a, 0, rect.minX, 0, e, rect.maxZ],
    controlPoints: [],
    rms: 0,
  };
}

/** Synthetic corner control points from a known affine, used to seed a layer before anyone clicks real ones. */
export function cornerControlPoints(
  affine: Calibration['affine'],
  widthPx: number,
  heightPx: number,
  label: string,
): ControlPoint[] {
  const cal: Calibration = { mode: 'fitted', affine, controlPoints: [] };
  return [
    [0, 0],
    [widthPx, 0],
    [0, heightPx],
    [widthPx, heightPx],
  ].map(([px, py]) => ({ px, py, ...pixelToWorld(cal, px, py), label }));
}
